import { style } from '@vanilla-extract/css';
import { calc } from '@vanilla-extract/css-utils';
import { themeVars } from '@customerio/pluma-components/css/theme-tokens';

/**
 * Styles for the activity-log JSON formatter. It reads like a code panel (subtle surface + mono
 * font) but the renderer stays a React tree so we can inject identifier/reserved Labels, entity
 * links, and humanized timestamps (which Pluma `CodeBlock` can't host). All colors use theme
 * `color` tokens (not a fixed palette) so the panel is theme-compatible in light and dark:
 * `text-code` / `text-information` / `text-accent` for the syntax colors.
 */

export const root = style(
  {
    backgroundColor: themeVars.color['overlay-tint'],
    color: themeVars.color['text-primary'],
    fontFamily: themeVars['font-family'].mono,
    fontSize: themeVars['font-size']['xxs'],
    lineHeight: 1.6,
    borderRadius: themeVars.radius['4'],
    padding: themeVars.space['200'],
    maxHeight: '400px',
    overflow: 'auto',
    whiteSpace: 'pre',
    tabSize: 2,
  },
  'log-json-formatter-root',
);

/** The copy button's styles, ported from Pluma `CodeBlock` with this panel's padding in place of its own. */
const padding = themeVars.space['200'];

/** Room on the right so the JSON never runs under the copy button (`CodeBlock` pads each line the same). */
export const rootWithCopyButton = style(
  { paddingRight: calc(padding).add(themeVars.space['350']).toString() },
  'log-json-formatter-root-with-copy-button',
);

export const stickyContainer = style(
  {
    display: 'flex',
    position: 'sticky',
    // 0, not `CodeBlock`'s padding: this panel pads itself, and a sticky offset is measured inside
    // that padding, so the padding would count twice and drop the button a line.
    top: 0,
    left: 0,
    // 0 height to avoid affecting layout, since the element is sticky (not absolute)
    height: 0,
    width: '100%',
    zIndex: 3,
  },
  'log-json-formatter-sticky-container',
);

export const copyToClipboardButton = style(
  {
    position: 'absolute',
    // As in `CodeBlock`: the 28px button is centered on the first 21px line.
    top: '-3.5px',
    // `CodeBlock` puts the button `padding - 3.5px` from its edge. This container sits inside the
    // panel's padding, which `rootWithCopyButton` widens to `padding + 350`, so the same spot is
    // `-(350 + 3.5px)` from the container's edge.
    right: calc(themeVars.space['350']).add('3.5px').negate().toString(),
  },
  'log-json-formatter-copy-to-clipboard-button',
);

/** Nested object/array indentation with a subtle guide. */
export const nested = style(
  {
    listStyle: 'none',
    margin: 0,
    paddingLeft: themeVars.space['200'],
    borderLeft: `${themeVars.border['width-0125']} solid ${themeVars.color['scope-neutral-subtle-border']}`,
  },
  'log-json-formatter-nested',
);

export const row = style({ display: 'block' }, 'log-json-formatter-row');

export const punctuation = style({ color: themeVars.color['text-secondary'] }, 'log-json-formatter-punctuation');
export const propKey = style({ color: themeVars.color['scope-action-text'] }, 'log-json-formatter-prop');
export const valueString = style({ color: themeVars.color['text-code'] }, 'log-json-formatter-string');
export const valueNumber = style({ color: themeVars.color['scope-information-text'] }, 'log-json-formatter-number');
export const valueBool = style({ color: themeVars.color['scope-caution-text'] }, 'log-json-formatter-bool');
export const valueNull = style({ color: themeVars.color['text-secondary'] }, 'log-json-formatter-null');

/** Humanized timestamp annotation, muted, trailing the raw value. */
export const timestamp = style(
  {
    color: themeVars.color['text-secondary'],
    marginLeft: themeVars.space['100'],
  },
  'log-json-formatter-timestamp',
);

/** Inline wrapper so the Pluma Label sits neatly between the key and the colon. */
export const badge = style(
  {
    display: 'inline-flex',
    verticalAlign: 'middle',
    margin: `0 ${themeVars.space['100']}`,
  },
  'log-json-formatter-badge',
);
